import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, IconButton, Divider } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// ---------- Helpers ----------

// Remove <img> and some Word/VML image remnants.
function removeImgTags(html) {
    if (!html) return html;

    let out = html.replace(/<img\b[^>]*>/gi, '');
    out = out.replace(/<v:imagedata\b[^>]*\/?>/gi, '');
    out = out.replace(/<o:shapedefaults\b[^>]*>[\s\S]*?<\/o:shapedefaults>/gi, '');

    return out;
}

function getPlainTextFromHtml(html) {
    const noImgHtml = removeImgTags(html);
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = noImgHtml;
    return tempDiv.textContent || tempDiv.innerText || '';
}

function normalizeText(text) {
    if (!text) return '';
    return text
        .replace(/\r/g, '')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

function truncateText(text, limit) {
    if (!text) return '';
    if (text.length <= limit) return text;
    return text.slice(0, limit) + '…';
}

// Simple HTML detection
const looksLikeHtml = (txt) => /<\/?[a-z][\s\S]*>/i.test(txt || '');

// ---------- Component ----------

function SearchResult({ result }) {
    // Separate expand states
    const [descExpanded, setDescExpanded] = useState(false);
    const [solExpanded, setSolExpanded] = useState(false);

    useEffect(() => {
        setDescExpanded(false);
        setSolExpanded(false);
    }, [result]);

    const PREVIEW_LEN = 100; // <— hier kannst du reduzieren/erhöhen (z.B. 120)

    // Robust created date handling (backend may send createdDate OR created_time)
    const createdValue = result.createdDate || result.created_time || null;

    // Determine whether we should treat description/solution as HTML
    // Sunrise SQL: you currently send plain in description/solution,
    // but might later send HTML. So we detect per field.
    const descriptionRaw = result.description || '';
    const solutionRaw = result.solution || '';

    const descriptionIsHtml =
        result.type === 'Online Help' ||
        result.type === 'DevOps' ||
        result.type === 'Sunrise Ticket' ||
        looksLikeHtml(descriptionRaw);

    const solutionIsHtml = looksLikeHtml(solutionRaw);

    // Prepare description content
    let descriptionPlain = normalizeText(
        descriptionIsHtml ? getPlainTextFromHtml(descriptionRaw) : descriptionRaw
    );

    let descriptionSanitizedHtml = '';
    if (descriptionIsHtml) {
        descriptionSanitizedHtml = removeImgTags(descriptionRaw);
    }

    // Prepare solution content
    let solutionPlain = normalizeText(
        solutionIsHtml ? getPlainTextFromHtml(solutionRaw) : solutionRaw
    );

    let solutionSanitizedHtml = '';
    if (solutionIsHtml) {
        solutionSanitizedHtml = removeImgTags(solutionRaw);
    }

    const showToggleDescription = (descriptionPlain || '').length > PREVIEW_LEN;
    const showToggleSolution = (solutionPlain || '').length > PREVIEW_LEN;

    // Reusable toggle button (top & bottom)
    const ToggleButton = ({ expanded, onClick, labelExpanded = 'Collapse', labelCollapsed = 'Expand' }) => (
        <IconButton onClick={onClick} size="small" title={expanded ? labelExpanded : labelCollapsed}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
    );

    return (
        <Card style={{ margin: 10, textAlign: 'left' }}>
            <CardContent>
                {/* Tags */}
                <div style={{ marginBottom: 5 }}>
                    <Typography
                        variant="body2"
                        style={{
                            display: 'inline-block',
                            marginRight: 5,
                            padding: '3px 8px',
                            color: '#ffffff',
                            backgroundColor: '#001738',
                            borderRadius: 5
                        }}
                    >
                        {result.type}
                    </Typography>

                    {result.tfsType && (
                        <Typography
                            variant="body2"
                            style={{
                                display: 'inline-block',
                                marginRight: 5,
                                padding: '3px 8px',
                                color: '#ffffff',
                                backgroundColor: '#f04e23',
                                borderRadius: 5
                            }}
                        >
                            {result.tfsType}
                        </Typography>
                    )}

                    <Typography
                        variant="body2"
                        style={{ display: 'inline-block', marginRight: 5, padding: '3px 8px' }}
                    >
                        {Math.round(result.score * 10) / 10}% Match
                    </Typography>
                </div>

                {/* Title */}
                <Typography
                    style={{
                        fontFamily: 'Inter',
                        fontSize: 18,
                        marginBottom: 5,
                        fontWeight: 500
                    }}
                >
                    <a
                        href={result.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            color: '#001738',
                            textDecoration: 'none',
                            borderBottom: '1px solid transparent',
                            transition: 'border-color 0.3s',
                            cursor: 'pointer',
                            display: 'inline-block'
                        }}
                        onMouseEnter={(e) => (e.target.style.borderBottom = '1px solid #001738')}
                        onMouseLeave={(e) => (e.target.style.borderBottom = '1px solid transparent')}
                    >
                        {result.title?.endsWith('.') ? result.title.slice(0, -1) : result.title}
                    </a>
                </Typography>

                {/* Optional Area */}
                {result.areaPath && (
                    <Typography style={{ fontFamily: 'Inter', fontSize: 13 }}>
                        <b>Area:</b> {result.areaPath}
                    </Typography>
                )}

                {/* Created */}
                {createdValue && (
                    <Typography style={{ marginBottom: 5, fontFamily: 'Inter', fontSize: 13 }}>
                        <b>Created:</b> {createdValue}
                    </Typography>
                )}

                {/* Ticket number */}
                {result.ticket_number && (
                    <Typography style={{ marginBottom: 5, fontFamily: 'Inter', fontSize: 13 }}>
                        <b>Ticket:</b> {result.ticket_number}
                    </Typography>
                )}

                {/* DESCRIPTION */}
                <div style={{ marginTop: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Typography style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 600 }}>
                            Description
                        </Typography>

                        {showToggleDescription && (
                            <ToggleButton
                                expanded={descExpanded}
                                onClick={() => setDescExpanded((v) => !v)}
                            />
                        )}
                    </div>

                    <Typography style={{ fontFamily: 'Inter', fontSize: 15, marginTop: 4, whiteSpace: 'pre-wrap' }}>
                        {descriptionIsHtml ? (
                            descExpanded ? (
                                <span dangerouslySetInnerHTML={{ __html: descriptionSanitizedHtml }} />
                            ) : (
                                <>{truncateText(descriptionPlain, PREVIEW_LEN)}</>
                            )
                        ) : (
                            <>{descExpanded ? descriptionPlain : truncateText(descriptionPlain, PREVIEW_LEN)}</>
                        )}
                    </Typography>

                    {/* Bottom toggle */}
                    {showToggleDescription && descExpanded && (
                        <div style={{ marginTop: 4 }}>
                            <ToggleButton
                                expanded={descExpanded}
                                onClick={() => setDescExpanded(false)}
                                labelExpanded="Collapse"
                                labelCollapsed="Expand"
                            />
                        </div>
                    )}
                </div>

                {/* SOLUTION */}
                {solutionPlain && (
                    <>
                        <Divider style={{ marginTop: 10, marginBottom: 10 }} />

                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Typography style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 600 }}>
                                    Solution
                                </Typography>

                                {showToggleSolution && (
                                    <ToggleButton
                                        expanded={solExpanded}
                                        onClick={() => setSolExpanded((v) => !v)}
                                    />
                                )}
                            </div>

                            <Typography style={{ fontFamily: 'Inter', fontSize: 13, marginTop: 4, whiteSpace: 'pre-wrap' }}>
                                {solutionIsHtml ? (
                                    solExpanded ? (
                                        <span dangerouslySetInnerHTML={{ __html: solutionSanitizedHtml }} />
                                    ) : (
                                        <>{truncateText(solutionPlain, PREVIEW_LEN)}</>
                                    )
                                ) : (
                                    <>{solExpanded ? solutionPlain : truncateText(solutionPlain, PREVIEW_LEN)}</>
                                )}
                            </Typography>

                            {/* Bottom toggle */}
                            {showToggleSolution && solExpanded && (
                                <div style={{ marginTop: 4 }}>
                                    <ToggleButton
                                        expanded={solExpanded}
                                        onClick={() => setSolExpanded(false)}
                                        labelExpanded="Collapse"
                                        labelCollapsed="Expand"
                                    />
                                </div>
                            )}
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}

export default SearchResult;